import { GenreDto } from '@cineamo/api/models/movie/GenreDto.types';
import { ReleaseDateType } from '@cineamo/api/models/movie/ReleaseDateType.types';
import { MovieDto as MovieV1Dto } from '@cineamo/api/models/movie/v1/MovieDto.types';
import { MovieDto as MovieV2Dto } from '@cineamo/api/models/movie/v2/MovieDto.types';
import { VideoType } from '@cineamo/api/models/movie/VideoType.types';

type MovieDto = MovieV1Dto | MovieV2Dto;

import { VideoSites } from '@/constants/tmdb-constants';
import { VIMEO_THUMBNAIL_IMAGE_DEFAULT, VIMEO_VIDEO_URL } from '@/constants/vimeo-constants';
import { YOUTUBE_THUMBNAIL_IMAGE_MAX_RES, YOUTUBE_VIDEO_URL } from '@/constants/youtube-constants';

import { VideoDtoEnhanced } from '@cineamo/api/helpers/movie-helper';

// Re-exported so app code keeps importing GRETA-accessibility and the age-rating badge from
// @/helpers/movie-helper (the app-local convention) while using the single shared implementation.
export { hasMovieAccessibilityFeatures } from '@cineamo/api/helpers/movie-helper';
export { getAgeRatingIcon } from '@cineamo/ui/src/helpers/movie-helper';

import { ISO_3166_1_LOCALE } from '@cineamo/ui/src/helpers/locale-helper';

import { dateTodayMinus } from '@/utils/date-helper';

/** A movie counts as newly released while its release date is within the last 7 days. */
export function isMovieReleaseDateNew(movieReleaseDate?: string | null): boolean {
    if (!movieReleaseDate) {
        return false;
    }
    return new Date(movieReleaseDate) > dateTodayMinus(7);
}

/** Whether a movie has at least one of the selected genre ids — used by the program genre filter. */
export function hasMatchingGenre(genres: GenreDto[] | undefined, activeGenreIds: string[]): boolean {
    if (activeGenreIds.length === 0) {
        return true;
    }
    return !!genres?.some((genre) => activeGenreIds.includes(genre.id.toString()));
}

export function getMovieEnhancedVideos(
    movie?: MovieDto,
    options?: { seekToSeconds?: number; locale?: string }
): VideoDtoEnhanced[] {
    if (!movie?.videos?.results) return [];

    // Map videos to include full URL and thumbnail URL
    const videos: VideoDtoEnhanced[] = movie.videos.results.flatMap((video) => {
        return {
            ...video,
            fullUrl:
                video.site === VideoSites.Vimeo
                    ? VIMEO_VIDEO_URL(video.key!, options?.seekToSeconds ?? 0)
                    : YOUTUBE_VIDEO_URL(video.key!, options?.seekToSeconds ?? 0),
            thumbnailUrl:
                video.site === VideoSites.Vimeo
                    ? VIMEO_THUMBNAIL_IMAGE_DEFAULT(video.key!)
                    : YOUTUBE_THUMBNAIL_IMAGE_MAX_RES(video.key!)
        };
    });

    let videosToReturn: VideoDtoEnhanced[] = [];

    // Filter videos based on locale
    if (options?.locale) {
        videosToReturn = videos.filter((video) => !video.iso6391 || video.iso6391 === options.locale);
    }

    if (videosToReturn.length <= 0) {
        videosToReturn = videos.filter((video) => !video.iso6391 || video.iso6391 === movie.originalLanguage || 'de');
    }

    // Sort videos, ensuring trailers come first
    return videosToReturn.sort((a, b) => {
        if (a.type === VideoType.Trailer && b.type !== VideoType.Trailer) return -1;
        if (a.type !== VideoType.Trailer && b.type === VideoType.Trailer) return 1;
        return 0;
    });
}

export function getMovieAgeRating(
    movie: MovieDto | null | undefined,
    countryCode?: ISO_3166_1_LOCALE,
    releaseDateType: ReleaseDateType = ReleaseDateType.THEATRICAL
): string | undefined {
    if (!countryCode) {
        return undefined;
    }

    const releaseDatesForLocale = movie?.releaseDates?.results?.find(
        (result) => result.iso31661?.toLowerCase() === countryCode.toLowerCase()
    )?.releaseDates;

    const releaseDateForType = releaseDatesForLocale?.find(
        (releaseDate) => releaseDate.type === releaseDateType
    )?.certification;

    return (
        releaseDateForType ??
        releaseDatesForLocale?.find((releaseDate) => releaseDate.type === ReleaseDateType.THEATRICAL_LIMITED)
            ?.certification ??
        releaseDatesForLocale?.find((releaseDate) => releaseDate.type === ReleaseDateType.PREMIERE)?.certification ??
        releaseDatesForLocale?.find((releaseDate) => releaseDate.type === ReleaseDateType.PHYSICAL)?.certification ??
        releaseDatesForLocale?.find(
            (releaseDate) => releaseDate.certification != null && releaseDate.certification.length > 0
        )?.certification
    );
}
